// React and hooks
import { useState, useEffect, useContext } from "react";

// Third-party libraries
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

// MUI components
import { Box, Card, Typography, Button, Grid, Stack } from "@mui/material";

// Contexts
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Services
import { httpGet } from "../../services/commonService";

// Custom components
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import RewardDialog from "./RewardDialog";

import { selectUserDetail } from "../../redux/slice/userSlice";

const Rewards = () => {
  const authenticationState = useContext(AuthenticationContext);
  const userDetail = useSelector(selectUserDetail);
  const { t } = useTranslation();
  const [snack, closeSnack, showSnackbar] = SnackState();
  const testimonials =
    t("RewardPage.Testimonials", { returnObjects: true }) || [];

  const [state, setState] = useState({
    isPopupOpen: false,
    isSecondPopupOpen: false,
    isPageRedirectAfterSuccess: false,
    textRewards: t("RewardPage.RewardTopTitle"),
    isRewardsQualified: true,
    isAccessAPIAllowed: false,
    videoLink1: "",
    videoLink2: "",
    accessAPIId: 0,
    isUpgradePopupOpen: false,
    refURL: "",
    refID: "",
    isPurchasedPackage: false,
    isConsultantPackagePurchased: false,
  });

  useEffect(() => {
    checkRewardPageAccessCriteria();
  }, []);

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      setState((prevState) => ({
        ...prevState,
        refID: userDetail?.UserAccountDetails?.DirectSponsorDetails.DSAcctID,
        isPurchasedPackage:
          userDetail?.PurchasePackageDetails?.IsBYLDerPackagePurchased,
        isConsultantPackagePurchased:
          userDetail?.PurchasePackageDetails?.IsConsultantPackagePurchased,
        refURL:
          userDetail?.UserAccountDetails?.DirectSponsorDetails
            .DSReplicationSiteName,
      }));
    }
  }, [userDetail?.UserAccountDetails?.AcctID]);

  const checkRewardPageAccessCriteria = async () => {
    try {
      const response = await httpGet(
        urlConstant.Rewards.CheckRewardPageAccessCriteria,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const responseData = response.Value.Data;
        setState((prevState) => ({
          ...prevState,
          isRewardsQualified: responseData.IsRewardsQualified,
          isAccessAPIAllowed: responseData.IsAccessAPIAllowed,
          videoLink1: responseData.VideoLink1,
          videoLink2: responseData.VideoLink2,
          accessAPIId: responseData.AccessAPIId,
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleNonQualifiedNonExistingCustomer = () => {
    setState((prevState) => ({ ...prevState, isPopupOpen: true }));
  };

  const handleNonQualifiedExistingCustomer = () => {
    setState((prevState) => ({ ...prevState, isPopupOpen: true }));
  };

  const handleQualifiedExistingCustomer = async () => {
    try {
      const checkRewardPageAccessCriteriaURL = `${urlConstant.Rewards.ReceiveMyDiscount}`;
      const response = await httpGet(
        checkRewardPageAccessCriteriaURL,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const responseData = response.Value.Data;
        window.open(responseData.SSOLink, "_blank");

        showSnackbar(response.description, "success");
      } else {
        showSnackbar(response.description, "error");
      }
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  const handleQualifiedNonExistingCustomer = async () => {
    try {
      const response = await httpGet(
        urlConstant.Rewards.CheckAccessAPI,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        setState((prevState) => ({
          ...prevState,
          isSecondPopupOpen: true,
        }));
      } else {
        showSnackbar(response.description, "error");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const packagePurchase = () => {
    setState((prevState) => ({
      ...prevState,
      isUpgradePopupOpen: true,
    }));
  };

  const handleCancelPopupModal = () => {
    setState((prevState) => ({ ...prevState, isPopupOpen: false }));
  };

  const handleCancelSecondPopupModal = () => {
    setState((prevState) => ({ ...prevState, isSecondPopupOpen: false }));
    window.location.reload();
  };

  const handleClosePopup = () => {
    setState((prevState) => ({ ...prevState, isUpgradePopupOpen: false }));
  };

  return (
    <>
      <Typography color="secondary.light" variant="h4" fontWeight={600}>
        {t("RewardPage.Header")}
      </Typography>
      <Box
        sx={(theme) => ({
          position: "relative",
          zIndex: 0,
          p: { xs: "10px", sm: "20px" },
          my: "20px",
          background: theme.palette.background.cardGradient,
          borderRadius: "20px",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage:
              'url("https://byldstorageprod.blob.core.windows.net/byldcontainer-prod/backoffice-assets/rewards-page-background.png")',
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top center",
            backgroundSize: "cover",
            filter: "blur(2px)",
            opacity: 0.5,
            zIndex: -1,
          },
        })}
      >
        <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />

        <Grid
          container
          spacing={2}
          justifyContent="center"
          mt={{ xs: "0px", sm: 2 }}
        >
          {state.isAccessAPIAllowed === false && state.videoLink1 !== "" && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <iframe
                  title="Video 1 Account Not Created"
                  src={state.videoLink1}
                  allow="autoplay; fullscreen; picture-in-picture"
                  style={{
                    width: "100%",
                    border: 0,
                    aspectRatio: "77/44",
                    padding: "10px",
                    borderRadius: "10px",
                    background: "var(--secondary-main)",
                  }}
                  allowFullScreen
                />
              </Box>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            {state.videoLink2 !== "" && (
              <Box>
                <iframe
                  title="Video 2 Account Created"
                  src={state.videoLink2}
                  allow="autoplay; fullscreen; picture-in-picture"
                  style={{
                    width: "100%",
                    border: 0,
                    aspectRatio: "77/44",
                    padding: "10px",
                    borderRadius: "10px",
                    background: "var(--secondary-main)",
                  }}
                  allowFullScreen
                />
              </Box>
            )}
          </Grid>
        </Grid>

        <Card
          sx={(theme) => ({
            p: { xs: "20px", sm: "25px 30px" },
            mt: 2,
            mb: 2,
            background: theme.palette.background.cardGradient,
            position: "relative",
            border: "1px solid var(--headerbg)",
            borderRadius: "12px",
          })}
        >
          <Typography
            variant="body1"
            color="secondary.text"
            sx={{ lineHeight: 1.8 }}
          >
            {t("RewardPage.SummaryText")}
          </Typography>
        </Card>

        <Stack
          flexDirection={"row"}
          flexWrap={"wrap"}
          gap={"20px"}
          mt={4}
          justifyContent={"center"}
          textAlign="center"
        >
          {state.isAccessAPIAllowed && state.isRewardsQualified && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleQualifiedExistingCustomer}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                fontSize: "1rem",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {t("RewardPage.ReceiveDiscount")}
            </Button>
          )}

          {state.isAccessAPIAllowed === false &&
            state.isRewardsQualified === false && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleNonQualifiedNonExistingCustomer}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "10px",
                  fontSize: "1rem",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {t("RewardPage.SignUp")}
              </Button>
            )}

          {state.isAccessAPIAllowed === false &&
            state.isRewardsQualified === true && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleQualifiedNonExistingCustomer}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "10px",
                  fontSize: "1rem",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {t("RewardPage.SignUp")}
              </Button>
            )}

          {state.isAccessAPIAllowed && state.isRewardsQualified === false && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleNonQualifiedExistingCustomer}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                fontSize: "1rem",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {t("RewardPage.SignUp")}
            </Button>
          )}
        </Stack>

        <Card
          sx={(theme) => ({
            p: { xs: "10px", sm: "20px" },
            mt: 4,
            mb: 2,
            background: theme.palette.background.cardGradient,
            position: "relative",
            border: "1px solid var(--headerbg)",
            borderRadius: "12px",
          })}
        >
          <Typography
            variant="h5"
            align="center"
            sx={{ mb: 3, fontWeight: 700 }}
          >
            {t("RewardPage.TestimonialsHeading")}
          </Typography>

          <Grid container className="cm-testimonials">
            {testimonials.map((testimonial, index) => (
              <Grid
                py={{ xs: "20px", sm: "0" }}
                sx={(theme) => ({
                  borderRight: "1px solid var(--secondary-main)",
                  ":last-of-type": { borderRight: "none" },
                  [theme.breakpoints.down(767)]: {
                    ":last-of-type": { borderBottom: "none" },
                    borderRight: "none",
                    borderBottom: "1px solid var(--secondary-main)",
                  },
                })}
                size={{ xs: 12, sm: 6, md: 4 }}
              >
                <Box key={index} px={2} className="cm-testimonials-wrapper">
                  <Typography variant="body1" color="secondary.text">
                    {testimonial.message}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontStyle="italic"
                    color="secondary.light"
                    sx={{ mt: 1 }}
                  >
                    {testimonial.author}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        <>
          <RewardDialog
            type={dataConstant.DialogTypes.Purchase}
            open={state.isPopupOpen}
            onClose={handleCancelPopupModal}
            onPurchase={packagePurchase}
            t={t}
          />
          <RewardDialog
            type={dataConstant.DialogTypes.Success}
            open={state.isSecondPopupOpen}
            onClose={handleCancelSecondPopupModal}
            onReceiveDiscount={handleQualifiedExistingCustomer}
            t={t}
          />
          <RewardDialog
            type={dataConstant.DialogTypes.Upgrade}
            open={state.isUpgradePopupOpen}
            onClose={handleClosePopup}
            refURL={state.refURL}
            isConsultantPackagePurchased={state.isConsultantPackagePurchased}
            t={t}
          />
        </>
      </Box>
    </>
  );
};

export default Rewards;
